/**
 * User Privileges Management - Microsoft 365 Style
 * Comprehensive interface for managing direct user privileges (RBAC)
 */

import React, { useEffect, useState, useMemo } from 'react';
import { makeStyles, Text, Input, Button, Spinner, Badge } from '@fluentui/react-components';
import {
  SearchRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  ShieldCheckmarkRegular,
  PersonRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { PermissionGate } from '../../../components/common';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';
import type {
  Feature,
  FeaturePrivilege,
  UserPrivilege,
  UserWithPrivileges,
  GroupedPrivileges,
} from '../../../types/rbac.types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    display: 'block',
    marginBottom: '4px',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
    marginTop: '4px',
  },
  container: {
    display: 'grid',
    gridTemplateColumns: '350px 1fr',
    gap: '24px',
    minHeight: '600px',
  },
  userListPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  searchBox: {
    width: '100%',
  },
  userList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '600px',
    overflowY: 'auto',
  },
  userCard: {
    padding: '12px',
    border: '1px solid #e1dfdd',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    ':hover': {
      backgroundColor: '#f3f2f1',
    },
  },
  userCardSelected: {
    backgroundColor: '#e6f7f5',
  },
  userName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#323130',
  },
  userRole: {
    fontSize: '12px',
    color: '#605e5c',
    marginTop: '4px',
  },
  privilegePanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#605e5c',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: '12px',
    borderBottom: '2px solid #e1dfdd',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
  },
  featureGroup: {
    marginBottom: '16px',
  },
  featureTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  privilegeList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '8px',
    paddingLeft: '16px',
  },
  privilegeItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    borderRadius: '4px',
    backgroundColor: '#f3f2f1',
  },
  privilegeItemInherited: {
    backgroundColor: '#f9f9f9',
    border: '1px dashed #d1d1d1',
  },
  privilegeItemDirect: {
    backgroundColor: '#e6f7f5',
    border: '1px solid #008272',
  },
  privilegeText: {
    fontSize: '13px',
    color: '#323130',
    flex: 1,
  },
  actionButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '16px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
  badge: {
    marginLeft: 'auto',
  },
});

export const UserPrivilegesManagementFluentUI: React.FC = () => {
  const styles = useStyles();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Data states
  const [users, setUsers] = useState<UserWithPrivileges[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [allPrivileges, setAllPrivileges] = useState<FeaturePrivilege[]>([]);
  const [userPrivileges, setUserPrivileges] = useState<UserPrivilege[]>([]);

  // UI states
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [modifiedPrivileges, setModifiedPrivileges] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [featuresRes, usersRes, userPrivsRes] = await Promise.all([
        ApiClient.get('/get-features'),
        ApiClient.get('/get-user-data'),
        ApiClient.get('/get-user-privileges'),
      ]);

      if (featuresRes.data.status === 'success') {
        const featuresData = featuresRes.data.data || [];
        setFeatures(featuresData);

        // Fetch privileges for each feature
        const privilegePromises = featuresData.map((feature: Feature) =>
          ApiClient.get(`/get-feature-privileges?feature_id=${feature.id}`)
        );
        const privilegeResults = await Promise.all(privilegePromises);
        const allPrivs: FeaturePrivilege[] = privilegeResults.flatMap(res => res.data.data || []);
        setAllPrivileges(allPrivs);
      }

      if (usersRes.data.status === 'success') {
        const usersData = usersRes.data.data || [];
        // Map users from /get-user-data response format
        const mappedUsers: UserWithPrivileges[] = usersData.map((u: any) => ({
          user_id: u.id,
          username: u.username || `${u.first_name} ${u.last_name}`,
          employee_id: u.employee_id || 0,
          first_name: u.first_name || '',
          last_name: u.last_name || '',
          role_id: u.role_id || 0,
          role_name: u.role_name || 'No Role',
          department_name: u.department_name || '',
          directPrivileges: [],
          inheritedPrivileges: [],
        }));
        setUsers(mappedUsers);
      }

      if (userPrivsRes.data.status === 'success') {
        setUserPrivileges(userPrivsRes.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const selectedUser = useMemo(() => {
    return users.find(u => u.user_id === selectedUserId) || null;
  }, [users, selectedUserId]);

  const selectedUserDirectPrivileges = useMemo(() => {
    if (!selectedUserId) {
      return new Set<number>();
    }
    return new Set(
      userPrivileges
        .filter(up => up.user_id === selectedUserId && up.status === 'active')
        .map(up => up.feature_privilege_id)
    );
  }, [userPrivileges, selectedUserId]);

  const groupedPrivileges: GroupedPrivileges[] = useMemo(() => {
    return features.map(feature => ({
      feature,
      privileges: allPrivileges.filter(p => p.feature_id === feature.id && p.status === 'active'),
    }));
  }, [features, allPrivileges]);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return users.filter(
      user =>
        user.first_name.toLowerCase().includes(query) ||
        user.last_name.toLowerCase().includes(query) ||
        user.role_name.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  const handleTogglePrivilege = (privilegeId: number) => {
    const newModified = new Set(modifiedPrivileges);
    if (newModified.has(privilegeId)) {
      newModified.delete(privilegeId);
    } else {
      newModified.add(privilegeId);
    }
    setModifiedPrivileges(newModified);
  };

  const handleSaveChanges = async () => {
    if (!selectedUserId || modifiedPrivileges.size === 0) {
      return;
    }

    setSaving(true);
    try {
      const promises: Promise<any>[] = [];

      modifiedPrivileges.forEach(privilegeId => {
        const hasPrivilege = selectedUserDirectPrivileges.has(privilegeId);
        if (hasPrivilege) {
          // Remove privilege
          promises.push(
            ApiClient.post('/remove-user-privilege', {
              user_id: selectedUserId,
              feature_privilege_id: privilegeId,
            })
          );
        } else {
          // Add privilege
          promises.push(
            ApiClient.post('/assign-user-privilege', {
              user_id: selectedUserId,
              feature_privilege_id: privilegeId,
            })
          );
        }
      });

      await Promise.all(promises);
      newToast('success', 'Privileges updated successfully');
      setModifiedPrivileges(new Set());
      await fetchData();
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setSaving(false);
  };

  const handleCancelChanges = () => {
    setModifiedPrivileges(new Set());
  };

  const hasChanges = modifiedPrivileges.size > 0;

  if (loading) {
    return (
      <div>
        <div className={styles.pageHeader}>
          <Text className={styles.pageTitle}>Manage User Privileges</Text>
        </div>
        <ContentCard>
          <div className={styles.loadingContainer}>
            <Spinner label="Loading privileges..." />
          </div>
        </ContentCard>
      </div>
    );
  }

  return (
    <PermissionGate privilege="manage-user-privileges" showDenied>
      <div>
        <div className={styles.pageHeader}>
          <Text className={styles.pageTitle}>Manage User Privileges</Text>
          <Text className={styles.pageSubtitle}>
            Assign direct privileges to users (overrides role-based privileges)
          </Text>
        </div>

        <ContentCard>
          <div className={styles.container}>
            {/* User List Panel */}
            <div className={styles.userListPanel}>
              <Input
                className={styles.searchBox}
                contentBefore={<SearchRegular />}
                placeholder="Search users..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />

              <div className={styles.userList}>
                {filteredUsers.map(user => (
                  <div
                    key={user.user_id}
                    className={`${styles.userCard} ${
                      selectedUserId === user.user_id ? styles.userCardSelected : ''
                    }`}
                    onClick={() => setSelectedUserId(user.user_id)}
                    style={
                      selectedUserId === user.user_id ? { border: '2px solid #008272' } : undefined
                    }
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <PersonRegular />
                      <div style={{ flex: 1 }}>
                        <Text className={styles.userName}>
                          {user.first_name} {user.last_name}
                        </Text>
                        <Text className={styles.userRole}>
                          {user.role_name}
                          {user.department_name && ` • ${user.department_name}`}
                        </Text>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Privilege Assignment Panel */}
            <div className={styles.privilegePanel}>
              {!selectedUser ? (
                <div className={styles.emptyState}>
                  <ShieldCheckmarkRegular style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <Text style={{ fontSize: '16px', fontWeight: '600' }}>
                    Select a user to manage privileges
                  </Text>
                  <Text style={{ fontSize: '14px', marginTop: '8px' }}>
                    Choose a user from the list to view and modify their direct privileges
                  </Text>
                </div>
              ) : (
                <>
                  <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <Text className={styles.sectionTitle}>
                        Direct Privileges for {selectedUser.first_name} {selectedUser.last_name}
                      </Text>
                      <Badge appearance="tint" color="brand">
                        {selectedUserDirectPrivileges.size} assigned
                      </Badge>
                    </div>

                    <Text style={{ fontSize: '13px', color: '#605e5c' }}>
                      Click privileges to add or remove them. Direct privileges override role-based
                      privileges.
                    </Text>

                    {groupedPrivileges.map(group => (
                      <div key={group.feature.id} className={styles.featureGroup}>
                        <div className={styles.featureTitle}>
                          <ShieldCheckmarkRegular />
                          {group.feature.name}
                          <Badge appearance="outline" size="small">
                            {group.privileges.length} privileges
                          </Badge>
                        </div>

                        <div className={styles.privilegeList}>
                          {group.privileges.map(privilege => {
                            const hasPrivilege = selectedUserDirectPrivileges.has(privilege.id);
                            const isModified = modifiedPrivileges.has(privilege.id);
                            const willHave = isModified ? !hasPrivilege : hasPrivilege;

                            return (
                              <div
                                key={privilege.id}
                                className={`${styles.privilegeItem} ${
                                  willHave ? styles.privilegeItemDirect : ''
                                }`}
                                onClick={() => handleTogglePrivilege(privilege.id)}
                                style={{ cursor: 'pointer' }}
                              >
                                {willHave ? (
                                  <CheckmarkCircleRegular style={{ color: '#008272' }} />
                                ) : (
                                  <DismissCircleRegular style={{ color: '#a19f9d' }} />
                                )}
                                <Text className={styles.privilegeText}>{privilege.title}</Text>
                                {isModified && (
                                  <Badge appearance="filled" color="warning" size="small">
                                    Modified
                                  </Badge>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  {hasChanges && (
                    <div className={styles.actionButtons}>
                      <Button
                        appearance="primary"
                        onClick={handleSaveChanges}
                        disabled={saving}
                        icon={saving ? <Spinner size="tiny" /> : undefined}
                      >
                        {saving ? 'Saving...' : `Save Changes (${modifiedPrivileges.size})`}
                      </Button>
                      <Button
                        appearance="secondary"
                        onClick={handleCancelChanges}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </ContentCard>
      </div>
    </PermissionGate>
  );
};

export default UserPrivilegesManagementFluentUI;
